/* eslint-disable import/no-cycle */
/* eslint-disable import/prefer-default-export,import/named */
import { ILine, IPoint } from '../../types/ark1.0/types';
import {
  compare,
  equalPoint, getDelta, getDistance, move, roundPoint,
} from '../point';
import { round } from '../round';
import { toFeet } from '../general';

export const createLine = (startPoint: IPoint, endPoint: IPoint): ILine => ({
  startPoint,
  endPoint,
});

export const getDistanceX = (line: ILine): number => round(Math.abs(line.startPoint.x - line.endPoint.x));
export const getDistanceY = (line: ILine): number => round(Math.abs(line.startPoint.y - line.endPoint.y));
export const isVertical = (line: ILine): boolean => getDistanceX(line) < getDistanceY(line);
export const toRight = (line: ILine): boolean => line.startPoint.x < line.endPoint.x;
export const toLeft = (line: ILine): boolean => line.startPoint.x > line.endPoint.x;
export const toUp = (line: ILine): boolean => line.startPoint.y > line.endPoint.y;
export const toDown = (line: ILine): boolean => line.startPoint.y < line.endPoint.y;
export const getPoints = (line: ILine): [IPoint, IPoint] => [
  line.startPoint, line.endPoint];

// line intercept math by Paul Bourke http://paulbourke.net/geometry/pointlineplane/
// Determine the intersection point of two line segments
// Return FALSE if the lines don't intersect
export const getIntersection = (line1: ILine, line2: ILine, rounded?: boolean): null | IPoint => {
  // Check if none of the lines are of length 0
  const [{ x: x1, y: y1 }, { x: x2, y: y2 }] = getPoints(line1);
  const [{ x: x3, y: y3 }, { x: x4, y: y4 }] = getPoints(line2);
  if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
    return null;
  }
  const denominator = ((y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1));
  // Lines are parallel
  if (denominator === 0) {
    return null;
  }
  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;
  // is the intersection along the segments
  if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
    return null;
  }
  // Return a object with the x and y coordinates of the intersection
  const x = x1 + ua * (x2 - x1);
  const y = y1 + ua * (y2 - y1);
  return rounded ? roundPoint({ x, y }) : { x, y };
};

export const checkPointIsOnLine = (line: ILine, checkPoint: IPoint, accuracy = 0): boolean => {
  const [linePt1, linePt2] = getPoints(line);
  const dist1 = round(getDistance(linePt1, checkPoint));
  const dist2 = round(getDistance(linePt2, checkPoint));
  const lineLength = round(getDistance(linePt1, linePt2));
  return (
    dist1 + dist2 >= lineLength - accuracy && dist1 + dist2 <= lineLength + accuracy
  );
};

export const getPointSide = (point: IPoint, line: ILine): 'startPoint' | 'endPoint' | undefined => {
  const [p0, p1] = getPoints(line);
  if (equalPoint(p0, point)) {
    return 'startPoint';
  }
  if (equalPoint(p1, point)) {
    return 'endPoint';
  }
  return undefined;
};

export const invertSide = (side: 'startPoint' | 'endPoint') => (
  side === 'startPoint' ? 'endPoint' : 'startPoint'
);

export const getCenter = (line: ILine) => {
  const [p0, p1] = getPoints(line);
  const delta = getDelta(p0, p1, 2);
  return roundPoint(move(p0, delta));
};

export const getQuarter = (line: ILine) => {
  const [p0, p1] = getPoints(line);
  const delta = getDelta(p0, p1, 4);
  return roundPoint(move(p0, delta));
};

export const getLength = (line: ILine): number => round(getDistance(line.startPoint, line.endPoint));
export const getLengthToFeet = (line: ILine): number => round(toFeet(getLength(line)));

export const getAngle = (line: ILine): number => {
  const [p1, p2] = getPoints(line).sort(compare);
  const delta = getDelta(p2, p1);
  const angle = (90 - (Math.atan2(delta.x, delta.y) * 180) / Math.PI) % 360;
  if (angle > 90 && angle < 180) {
    return angle - 180;
  }
  return angle;
};

export const getPointProjection = (point: IPoint, line: ILine): IPoint => {
  const [p1, p2] = getPoints(line);
  let x: number;
  let y: number;
  if (p1.x === p2.x) {
    x = p1.x;
    y = point.y;
  } else if (p1.y === p2.y) {
    x = point.x;
    y = p1.y;
  } else {
    const m1 = (p2.y - p1.y) / (p2.x - p1.x);
    const m2 = -1 / m1;
    x = (m1 * p1.x - m2 * point.x + point.y - p1.y) / (m1 - m2);
    y = m2 * (x - point.x) + point.y;
  }
  return roundPoint({ x, y });
};

export const getOffsetPointFromCenter = (line: ILine, height: number): IPoint => {
  const [p1] = getPoints(line);
  const center = getCenter(line);
  const half = getLength(createLine(center, p1));
  const [dX, dY] = [p1.x - center.x, center.y - p1.y];
  const coefficients = half / height;
  const x = dY / coefficients;
  const y = dX / coefficients;
  return { x, y };
};

const getDeltaByLinePoints = (line: ILine) => {
  const [p1, p2] = getPoints(line);
  return getDelta(p2, p1);
};

export const getOffsetPointFromPoint = (line: ILine, height: number, point: IPoint): IPoint => {
  const [p1] = getPoints(line);
  const half = getLength(createLine(point, p1));
  const [dX, dY] = [p1.x - point.x, point.y - p1.y];
  const coefficients = half / height;
  const x = dY / coefficients;
  const y = dX / coefficients;
  return { x, y };
};

export const getShiftedPoint = <T extends ILine>({ line, newLength }
: { line: T, newLength: number }) : IPoint => {
  const currentLength = getLength(line);
  const [p1] = getPoints(line);
  const delta = getDeltaByLinePoints(line);
  const x = round((delta.x / currentLength) * newLength);
  const y = round((delta.y / currentLength) * newLength);
  return roundPoint({
    x: p1.x + x, y: p1.y + y,
  });
};
